import { Grid, IconButton, MenuItem, Typography, Box } from "@mui/material";
import React, { useState } from "react";
import { VTextField } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { Scope } from "@unform/core";
import DeleteIcon from "@mui/icons-material/Delete";
import { PeopleService } from "../../../../shared/services/api/people/PeopleService";
import { globalMaskDocument } from "../../../../shared/utils";
import { useToast } from "../../../../shared/hooks";
import { ToastContent } from "../../../../shared/components";
import { Environment } from "../../../../shared/environment";

interface TenantSolidarityFieldsProps {
    index: number;
    onRemove: (index: number) => void;
    isLoading: boolean;
    formRef: any;
}

export const TenantSolidarityFields: React.FC<TenantSolidarityFieldsProps> = ({ index, onRemove, isLoading, formRef }) => {
    const toastHandler = useToast();
    const [loadingDoc, setLoadingDoc] = useState(false);

    const showToastError = (message: string) => {
        toastHandler.present({
            type: "error",
            position: "top-right",
            messageOrContent: <ToastContent title="Erro" content={message} />,
        });
    };

    const handleCPF = (value: string) => {
        const documento = value.replace(/\D/g, "").trim();

        if (documento.length < 11) return;
        setLoadingDoc(true);
        console.log(`Searching CPF for index ${index}: ${documento}`);

        // Mock for DEV environment
        if (Environment.AMBIENTE === "DEV" && Array.from({ length: 11 }, (_, index) => `0000000000${index}`).includes(documento)) {
            console.log(`Mock found for index ${index}`);
            setLoadingDoc(false);
            setTimeout(() => {
                formRef.current?.setFieldValue(`solidaryTenants[${index}].name`, `Solidário Teste ${documento.slice(-2)}`);
                formRef.current?.setFieldValue(`solidaryTenants[${index}].birthDate`, "2000-05-01");
                formRef.current?.setFieldValue(`solidaryTenants[${index}].motherName`, `Mãe Solidário ${documento.slice(-2)}`);
                formRef.current?.setFieldValue(`solidaryTenants[${index}].sex`, "male");
                formRef.current?.setFieldValue(`solidaryTenants[${index}].maritalStatus`, "single");
                formRef.current?.setFieldValue(`solidaryTenants[${index}].employmentRelationShip`, "student");
            }, 0);
            return;
        }

        PeopleService.getPersonDataByDocument(documento)
            .then((data) => {
                console.log(`Data found for index ${index}:`, data);
                if (!data) {
                    showToastError(`Documento ${globalMaskDocument(value)} não localizado.`);
                    setLoadingDoc(false);
                } else {
                    const { name, birthDate, motherName } = data.naturalPersonData || {};
                    console.log(`Setting fields for index ${index}:`, { name, birthDate, motherName });

                    setLoadingDoc(false);
                    setTimeout(() => {
                        formRef.current?.setFieldValue(`solidaryTenants[${index}].name`, name || "");
                        formRef.current?.setFieldValue(`solidaryTenants[${index}].birthDate`, birthDate || "");
                        formRef.current?.setFieldValue(`solidaryTenants[${index}].motherName`, motherName || "");
                        console.log("Form Data after set:", formRef.current?.getData());
                    }, 0);
                }
            })
            .catch((error) => {
                console.error(`Error searching CPF for index ${index}:`, error);
                showToastError(`Erro na pesquisa do documento ${globalMaskDocument(value)}`);
                setLoadingDoc(false);
            });
    };

    return (
        <Scope path={`solidaryTenants[${index}]`}>
            <Box
                sx={{
                    p: 3,
                    mb: 2,
                    bgcolor: "#f8f9fa",
                    borderRadius: 4,
                    border: "1px solid #e0e0e0",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    },
                }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box component="span" sx={{ bgcolor: "primary.main", color: "white", width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem" }}>
                                {index + 1}
                            </Box>
                            Locatário Solidário
                        </Typography>
                        <IconButton onClick={() => onRemove(index)} color="error" size="small" title="Remover locatário">
                            <DeleteIcon />
                        </IconButton>
                    </Grid>

                    <Grid item xs={12} sm={12} md={4}>
                        <VMaskTextField
                            fullWidth
                            label="CPF"
                            name="document"
                            mask="cpf"
                            disabled={isLoading || loadingDoc}
                            onBlur={(e) => handleCPF(e.target.value)}
                            InputProps={{ sx: { bgcolor: "white" } }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={12} md={8}>
                        <VTextField fullWidth label="Nome" name="name" disabled={isLoading || loadingDoc} InputProps={{ sx: { bgcolor: "white" } }} />
                    </Grid>

                    <Grid item xs={12} sm={12} md={4}>
                        <VMaskTextField fullWidth label="Celular" name="phone" mask="cellphone" disabled={isLoading} InputProps={{ sx: { bgcolor: "white" } }} />
                    </Grid>

                    <Grid item xs={12} sm={12} md={4}>
                        <VTextField fullWidth label="Data de Nascimento" name="birthDate" type="date" disabled={isLoading || loadingDoc} InputProps={{ sx: { bgcolor: "white" } }} />
                    </Grid>

                    <Grid item xs={12} sm={12} md={4}>
                        <VTextField fullWidth label="Nome da Mãe" name="motherName" disabled={isLoading || loadingDoc} InputProps={{ sx: { bgcolor: "white" } }} />
                    </Grid>

                    <Grid item xs={12} sm={12} md={4}>
                        <VTextField fullWidth label="Relação de Trabalho" name="employmentRelationShip" disabled={isLoading} select InputProps={{ sx: { bgcolor: "white" } }}>
                            <MenuItem value="retired">Aposentado</MenuItem>
                            <MenuItem value="freelance">Autônomo</MenuItem>
                            <MenuItem value="entrepreneur">Empresário</MenuItem>
                            <MenuItem value="student">Estudante</MenuItem>
                            <MenuItem value="civil_servant">Funcionário Público</MenuItem>
                            <MenuItem value="private_servant">Funcionário CLT</MenuItem>
                            <MenuItem value="liberal">Liberal</MenuItem>
                            <MenuItem value="rent_income">Renda Aluguéis</MenuItem>
                        </VTextField>
                    </Grid>

                    <Grid item xs={12} sm={12} md={4}>
                        <VTextField fullWidth label="Sexo" name="sex" select disabled={isLoading} InputProps={{ sx: { bgcolor: "white" } }}>
                            <MenuItem value="male">Masculino</MenuItem>
                            <MenuItem value="female">Feminino</MenuItem>
                        </VTextField>
                    </Grid>

                    <Grid item xs={12} sm={12} md={4}>
                        <VTextField fullWidth label="Estado Civil" name="maritalStatus" select disabled={isLoading} InputProps={{ sx: { bgcolor: "white" } }}>
                            <MenuItem value="single">Solteiro</MenuItem>
                            <MenuItem value="married">Casado</MenuItem>
                            <MenuItem value="widower">Viúvo</MenuItem>
                            <MenuItem value="divorced">Divorciado</MenuItem>
                            <MenuItem value="stable_union">União Estável</MenuItem>
                        </VTextField>
                    </Grid>
                </Grid>
            </Box>
        </Scope>
    );
};
